import { hex } from "@scure/base";
import { encode } from "cborg";

import type { GalleryItem, InscriptionAttributes, TraitValue } from "./types";

const PROPERTY_CHUNK_BYTES = 520;
const INSCRIPTION_ID = /^([0-9a-f]{64})i(\d+)$/i;

function attributesMap(attributes?: InscriptionAttributes): Map<number, unknown> | undefined {
  if (!attributes) return undefined;
  const result = new Map<number, unknown>();
  const title = attributes.title?.trim();
  if (title) result.set(0, title);
  const entries = Object.entries(attributes.traits ?? {}).filter(([key, value]) => key.trim() && validTrait(value));
  if (entries.length) result.set(1, new Map(entries));
  return result.size ? result : undefined;
}

function validTrait(value: unknown): value is TraitValue {
  return value === null || typeof value === "string" || typeof value === "boolean" || (typeof value === "number" && Number.isSafeInteger(value));
}

function galleryMaps(items: GalleryItem[]): { items: Map<number, unknown>[]; txids: Uint8Array } {
  const packed: number[] = [];
  const maps = items.map((item) => {
    const match = INSCRIPTION_ID.exec(item.inscriptionId.trim());
    if (!match) throw new Error(`Invalid gallery inscription ID: ${item.inscriptionId}`);
    const index = Number(match[2]);
    if (!Number.isSafeInteger(index) || index < 0) throw new Error(`Invalid gallery inscription index: ${match[2]}`);
    packed.push(...hex.decode(match[1] ?? "").reverse());
    const result = new Map<number, unknown>();
    const attributes = attributesMap(item.attributes);
    if (attributes) result.set(1, attributes);
    if (index) result.set(2, index);
    return result;
  });
  return { items: maps, txids: new Uint8Array(packed) };
}

export function propertyTags(attributes?: InscriptionAttributes, gallery: GalleryItem[] = []): [Uint8Array, Uint8Array][] | undefined {
  const properties = new Map<number, unknown>();
  const encodedAttributes = attributesMap(attributes);
  if (encodedAttributes) properties.set(1, encodedAttributes);
  if (gallery.length) {
    const encodedGallery = galleryMaps(gallery);
    properties.set(0, encodedGallery.items);
    properties.set(2, encodedGallery.txids);
  }
  if (!properties.size) return undefined;
  const payload = encode(properties);
  const tags: [Uint8Array, Uint8Array][] = [];
  for (let offset = 0; offset < payload.length; offset += PROPERTY_CHUNK_BYTES) {
    tags.push([new Uint8Array([17]), payload.slice(offset, offset + PROPERTY_CHUNK_BYTES)]);
  }
  return tags;
}

export async function brotliCompress(body: Uint8Array): Promise<Uint8Array> {
  const { default: brotliPromise } = await import("brotli-wasm");
  const brotli = await brotliPromise;
  return brotli.compress(body);
}

export function opReturnScript(value: string): Uint8Array {
  if (!value) throw new Error("OP_RETURN data cannot be empty");
  const bytes = new TextEncoder().encode(value);
  if (bytes.length > 80) throw new Error("OP_RETURN data is limited to 80 encoded bytes");
  if (bytes.length <= 75) return new Uint8Array([0x6a, bytes.length, ...bytes]);
  return new Uint8Array([0x6a, 0x4c, bytes.length, ...bytes]);
}

export function burnScript(message = ""): Uint8Array {
  let value: unknown = message;
  if (message.trim()) {
    try {
      value = JSON.parse(message) as unknown;
    } catch {
      value = message;
    }
  }
  const payload = message.trim() ? encode(value) : new Uint8Array();
  if (payload.length > 80) throw new Error("Burn message is limited to 80 CBOR bytes");
  if (payload.length <= 75) return new Uint8Array([0x6a, payload.length, ...payload]);
  return new Uint8Array([0x6a, 0x4c, payload.length, ...payload]);
}
